import { useCallback, useEffect } from 'react';

import IterableService from './IterableService';

interface UseInAppMessagesOptions {
  /** Whether the user is authenticated */
  authenticated: boolean | null;
  /** Whether to automatically fetch messages when authenticated */
  autoFetch?: boolean;
  /** Number of messages to fetch */
  count?: number;
  /** Interval between message displays in milliseconds */
  displayInterval?: number;
  /** Whether the Iterable SDK has been initialized */
  isInitialized?: boolean;
  /** Package name for the app (must match Iterable website integration identifier) */
  packageName?: string;
}

/**
 * Hook to manage in-app messages from Iterable.
 *
 * Uses the SDK's `display: 'immediate'` mode, which means the SDK handles
 * rendering and tracking of messages automatically.
 */
export function useInAppMessages({
  authenticated,
  isInitialized = false,
  packageName = 'metropolis-customer-web',
  count = 10,
  displayInterval = 5000,
  autoFetch = true,
}: UseInAppMessagesOptions) {
  const fetchMessages = useCallback(async () => {
    if (!authenticated || !isInitialized) {
      return;
    }

    try {
      // `request()` fetches messages and, because we initialized the SDK with
      // `display: 'immediate'`, triggers the SDK's own paint + tracking pipeline.
      const sdkResponse = IterableService.getInAppMessagesImmediate({
        count,
        packageName,
        displayInterval,
      });
      await sdkResponse.request();
    } catch (err) {
      // eslint-disable-next-line no-console -- Iterable fetch errors are non-fatal
      console.error('Failed to fetch in-app messages:', err);
    }
  }, [authenticated, isInitialized, count, packageName, displayInterval]);

  // Auto-fetch messages when authenticated and SDK is initialized
  useEffect(() => {
    if (autoFetch && authenticated && isInitialized) {
      fetchMessages();
    }
  }, [autoFetch, authenticated, isInitialized, fetchMessages]);

  return { fetchMessages };
}
